import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Trash2, RefreshCw, Search, Inbox } from "lucide-react";
import { Logo } from "../components/Logo";
import { Seo } from "../components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["Nauja", "Susisiekta", "Pamatuota", "Atlikta"];

const statusCls = {
  Nauja: "bg-neutral-200 text-neutral-900",
  Susisiekta: "bg-neutral-100 text-neutral-500",
  Pamatuota: "bg-neutral-300 text-neutral-800",
  Atlikta: "bg-neutral-900 text-white",
};

export default function Admin() {
  const [token, setToken] = useState(sessionStorage.getItem("artceiling_admin") || "");
  const [authed, setAuthed] = useState(!!sessionStorage.getItem("artceiling_admin"));
  const [input, setInput] = useState("");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");

  const load = useCallback(async (t) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/leads`, { headers: { "X-Admin-Key": t } });
      setLeads(res.data);
      setAuthed(true);
      sessionStorage.setItem("artceiling_admin", t);
    } catch {
      toast.error("Neteisingas prieigos raktas");
      setAuthed(false);
      sessionStorage.removeItem("artceiling_admin");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authed) load(token);
  }, [authed, load, token]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/leads/${id}`, { status }, { headers: { "X-Admin-Key": token } });
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
      toast.success("Būsena atnaujinta");
    } catch {
      toast.error("Nepavyko atnaujinti");
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/leads/${id}`, { headers: { "X-Admin-Key": token } });
      setLeads((ls) => ls.filter((l) => l.id !== id));
      toast.success("Užklausa ištrinta");
    } catch {
      toast.error("Nepavyko ištrinti");
    }
  };

  if (!authed) {
    return (
      <main data-testid="admin-login" className="flex min-h-screen items-center justify-center bg-navy-950 px-4">
        <Seo title="Administratoriaus zona — ART Ceiling" description="Administratoriaus zona." noindex />
        <div className="w-full max-w-sm rounded-3xl border border-white/10 bg-white/5 p-8 backdrop-blur">
          <div className="flex justify-center">
            <Logo dark />
          </div>
          <h1 className="mt-6 text-center font-heading text-lg font-bold text-white">Administratoriaus zona</h1>
          <form
            className="mt-6 space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              setToken(input);
              load(input);
            }}
          >
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="password"
                data-testid="admin-token-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Prieigos raktas"
                className="w-full rounded-xl border border-white/10 bg-white/10 py-3 pl-11 pr-4 text-sm text-white placeholder:text-slate-500 outline-none focus:border-sky-400"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-button"
              className="w-full rounded-full bg-sky-400 py-3 text-sm font-bold text-navy-950 transition-colors hover:bg-sky-300 disabled:opacity-60"
            >
              {loading ? "Jungiamasi..." : "Prisijungti"}
            </button>
          </form>
        </div>
      </main>
    );
  }

  const filtered = leads.filter(
    (l) =>
      l.name.toLowerCase().includes(query.toLowerCase()) ||
      l.phone.includes(query) ||
      (l.city || "").toLowerCase().includes(query.toLowerCase())
  );

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-slate-50 pb-20 pt-28">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold text-navy-900 sm:text-3xl">Užklausos</h1>
            <p className="mt-1 text-sm text-slate-500" data-testid="admin-leads-count">
              Iš viso: {leads.length}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                data-testid="admin-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Ieškoti vardo, tel., miesto"
                className="w-64 rounded-full border border-slate-200 bg-white py-2.5 pl-10 pr-4 text-sm outline-none focus:border-sky-500"
              />
            </div>
            <button
              onClick={() => load(token)}
              data-testid="admin-refresh-button"
              className="rounded-full border border-slate-200 bg-white p-2.5 text-slate-600 transition-colors hover:bg-slate-100"
              aria-label="Atnaujinti"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="mt-16 flex flex-col items-center text-slate-400" data-testid="admin-empty-state">
            <Inbox className="h-12 w-12" />
            <p className="mt-4 text-sm">Užklausų dar nėra.</p>
          </div>
        ) : (
          <div className="mt-8 overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
            <table className="w-full min-w-[760px] text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-5 py-4">Data</th>
                  <th className="px-5 py-4">Klientas</th>
                  <th className="px-5 py-4">Miestas</th>
                  <th className="px-5 py-4">Tipas / žinutė</th>
                  <th className="px-5 py-4">Būsena</th>
                  <th className="px-5 py-4" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((l, i) => (
                  <tr key={l.id} data-testid={`admin-lead-row-${i}`} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                    <td className="whitespace-nowrap px-5 py-4 text-slate-500">
                      {new Date(l.created_at).toLocaleDateString("lt-LT")}
                    </td>
                    <td className="px-5 py-4">
                      <div className="font-semibold text-navy-900">{l.name}</div>
                      <a href={`tel:${l.phone.replace(/\s/g, "")}`} className="text-xs text-sky-600">
                        {l.phone}
                      </a>
                      {l.email && <div className="text-xs text-slate-400">{l.email}</div>}
                    </td>
                    <td className="px-5 py-4 text-slate-600">{l.city || "—"}</td>
                    <td className="max-w-[220px] px-5 py-4">
                      <div className="truncate font-medium text-slate-700">{l.service || (l.type === "kontaktai" ? "Kontaktų žinutė" : "—")}</div>
                      <div className="truncate text-xs text-slate-400">{l.message || ""}</div>
                    </td>
                    <td className="px-5 py-4">
                      <select
                        value={l.status}
                        onChange={(e) => updateStatus(l.id, e.target.value)}
                        data-testid={`admin-lead-status-${i}`}
                        className={`cursor-pointer rounded-full border-0 px-3 py-1.5 text-xs font-bold outline-none ${statusCls[l.status] || "bg-slate-100 text-slate-600"}`}
                      >
                        {STATUSES.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <button
                        onClick={() => remove(l.id)}
                        data-testid={`admin-lead-delete-${i}`}
                        className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-500"
                        aria-label="Ištrinti"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </main>
  );
}
